import { Formik, Form, Field, type FormikProps } from "formik";
import { Input } from "@/components/ui/input";
import { LOGO } from "@/assets/images";
import { MessageBox } from "@/assets/svg";
import { useNavigate } from "react-router-dom";
import routesPath from "@/utils/routesPath";
import { useSelector } from "react-redux";
import type { RootStateType } from "@/redux/store";
import { forgotPasswordSchema } from "@/utils/schemas/auth";
import { useEffect, useState } from "react";
import { useForgotPasswordMutation } from "@/redux/services/auth/authApi";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
// import type { ErrorResponse } from '@/types/index'

const { LOGIN, DASHBOARD } = routesPath;

interface ForgotPasswordFormValues {
  email: string;
}

const ForgotPassword = () => {
  const user = useSelector((state: RootStateType) => state.auth.user);
  const [message, setMessage] = useState<boolean>(false);

  const navigate = useNavigate();

  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();

  const initialValues: ForgotPasswordFormValues = {
    email: "",
  };

  const handleSubmit = async (
    values: ForgotPasswordFormValues,
    {
      setSubmitting,
      resetForm,
    }: { setSubmitting: (isSubmitting: boolean) => void; resetForm: () => void }
  ) => {
    try {
      setMessage(false);
      await forgotPassword(values)
        .unwrap()
        .then(() => {
          setMessage(true);
          resetForm();
        })
        .catch((error) => {
          toast.error(error.data.message);
        });
    } catch (error: unknown) {
      const errorMessage =
        (error as { data?: { message?: string } })?.data?.message ||
        "Failed to send reset instructions";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      navigate(DASHBOARD);
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#F9F9F9] flex sm:items-center sm:justify-center p-4 sm:-10">
      <div className=" sm:bg-[#FFFFFF] sm:rounded-lg sm:shadow-sm px-4 pt-10 sm:p-8 w-full max-w-[793px] sm:px-[155px] sm:py-[64px]">
        {message && (
          <div className="text-[10px] sm:text-[16px] text-[#131515] bg-[#EAFFE591] border-1 border-[#F5F5F5] rounded-[4px] flex items-center gap-2 p-2 -mt-7 mb-7">
            <MessageBox className="w-[10px] h-[10px] sm:h-[16px] sm:w-[16px]" />
            <span>A link as been sent to the designated account type. </span>
          </div>
        )}
        <div className="flex justify-center mb-6">
          <img
            src={LOGO}
            alt="logo"
            className="h-[30px] w-[136px] md:w-[179px] max-w-[179px] md:h-[43px] md:max-h-[43px]"
          />
        </div>

        <div className="mb-11 ">
          <h1 className="text-[clamp(1.25rem,4vw,2.125rem)] w-[261px] md:text-[34px] font-semibold text-[#4C4C4C] md:w-[364px]">
            Let's Get You Back <br /> Into Your Account.
          </h1>

          <span className="text-[#6B7280] text-xs md:text-sm font-normal DM-Sans">
            Kindly enter the registered email / phone number{" "}
            <br className="sm:hidden" /> linked to your account
          </span>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={forgotPasswordSchema}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            touched,
            // isSubmitting,
          }: FormikProps<ForgotPasswordFormValues>) => (
            <Form className="">
              <div className="space-y-[23px]">
                <div>
                  <Field name="email">
                    {({
                      field,
                    }: {
                      field: {
                        name: string;
                        value: string;
                        onChange: (
                          e: React.ChangeEvent<HTMLInputElement>
                        ) => void;
                        onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
                      };
                    }) => (
                      <Input
                        {...field}
                        id="email"
                        label="Email/Phone number"
                        type="text"
                        placeholder="Enter email address or phone number"
                        containerClass=""
                        handleBlur={(e) =>
                          field.onBlur(e as React.FocusEvent<HTMLInputElement>)
                        }
                        touched={touched.email}
                        error={errors.email}
                      />
                    )}
                  </Field>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full hover:bg-primary/90 text-white font-medium py-3 rounded-lg"
                  loading={isLoading}
                  loadingText="Submitting..."
                >
                  Submit
                </Button>
              </div>

              <div className="text-sm mt-2" onClick={() => navigate(LOGIN)}>
                <span className="text-gray-600">
                  <Button
                    type="button"
                    variant="link"
                    className="text-gray-800 hover:text-green-700 font-semibold hover:underline cursor-pointer !p-0"
                  >
                    Back to login
                  </Button>
                </span>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ForgotPassword;
